<script>
import MottoIcon from '@components/motto-icon.vue'
import ResourceIcon from '@components/resources/resource-icon.vue'
import FavoriteHeart from '@components/favorite-heart.vue'
import LoaderIcon from '@components/loader-icon.vue'
import { addFavoriteResource, deleteFavoriteResource } from '@src/api/resources'
import { downloadResourceAssetMixin } from '@src/mixins/downloadResourceAssetMixin'
import { resourcesComputed } from '@state/helpers'
import { isMobileMixin } from '@src/mixins/isMobileMixin'

export default {
  components: {
    MottoIcon,
    ResourceIcon,
    FavoriteHeart,
    LoaderIcon,
  },
  mixins: [downloadResourceAssetMixin, isMobileMixin],
  props: {
    resource: {
      type: Object,
      required: true,
    },
    minimalDesign: {
      type: Boolean,
    },
  },
  data() {
    return {
      isTransparent: false,
      loadingFavorite: false,
    }
  },
  computed: {
    ...resourcesComputed,
    isFavorite() {
      return this.isResourceFavorite(this.resource.resourceId)
    },

    resourceIcon() {
      if (this.resource.assetType === 'video') {
        return 'video'
      }

      if (this.resource.isDownloadable && this.resource.assetFileExtension) {
        return 'redFile'
      }

      if (this.resource.resourceType === 'article') {
        return 'blueFile'
      }

      return 'redFile'
    },

    resourceFileExtension() {
      if (this.resource.isDownloadable && this.resource.assetFileExtension) {
        return this.resource.assetFileExtension.toUpperCase()
      }

      if (this.resource.resourceType === 'article') {
        return 'TXT'
      }

      if (this.resource.resourceType === 'package') {
        return 'ZIP'
      }

      return ''
    },
  },
  methods: {
    async favoriteResource() {
      await addFavoriteResource({ resourceId: this.resource.resourceId })
      this.$store.commit('resources/FAVORITE_RESOURCE_BY_ID', this.resource.resourceId)
    },

    async unfavoriteResource() {
      await deleteFavoriteResource({ resourceId: this.resource.resourceId })
      this.$store.commit('resources/UNFAVORITE_RESOURCE_BY_ID', this.resource.resourceId)
    },

    async handleHeartClick() {
      if (this.loadingFavorite) {
        return
      }
      this.loadingFavorite = true
      if (this.isFavorite) {
        await this.unfavoriteResource()
      } else {
        await this.favoriteResource()
      }
      this.loadingFavorite = false
    },

    async downloadAsset() {
      await this.downloadResourceAsset({
        resourceId: this.resource.resourceId,
        assetUrl: this.resource.assetUrl,
      })
    },

    viewResource() {
      if (this.isMobile) {
        this.$router.push({
          name: 'resource',
          params: {
            resourceId: this.resource.resourceId.toString(),
          },
        })
        return
      }
      this.$store.commit('resources/SET_SELECTED_RESOURCE_ID', this.resource.resourceId)
      this.$bvModal.show('view-resource-modal')
    },
  },
}
</script>

<template>
  <div
    :class="{
      [$style.resourceBlockContainer]: true,
      [$style.minimalDesign]: minimalDesign,
      'light-transparent': isTransparent,
    }"
    data-resource-block
    @mouseover="isTransparent = true"
    @mouseout="isTransparent = false"
    @click="viewResource">
    <div :class="$style.heading">
      <div :class="$style.titleContainer">
        <ResourceIcon v-if="true" :name="resourceIcon" :file-extension="resourceFileExtension" />
        <p data-resource-block-title class="button-text color-darkest-gray" :class="$style.headTitle">
          {{ resource.title }}
        </p>
      </div>
      <LoaderIcon v-if="loadingFavorite" class="d-inline" :class="$style.heartIcon" />
      <FavoriteHeart
        v-else-if="resource.resourceId"
        :class="$style.heartIcon"
        :is-favorite="isFavorite"
        @handleHeartClick="handleHeartClick" />
    </div>
    <div :class="$style.body">
      <p class="p2" :class="$style.bodyExcerpt">{{ resource.excerpt }}</p>
      <p class="button-text color-darkest-gray" :class="$style.bodyTitle">{{ resource.title }}</p>
    </div>
    <div :class="$style.footer">
      <div class="tagsContainer" :class="$style.tagsContainer">
        <span v-for="tag in resource.tags" :key="tag" class="tag-text">{{ tag }}</span>
      </div>
      <div v-show="resource.isDownloadable" :class="$style.downloadButton" @click.stop="downloadAsset">
        <MottoIcon name="Download" icon-color="gray" />
      </div>
      <div v-show="resource.isDownloadable" :class="$style.downloadButtonMinimalInMobile" @click.stop="downloadAsset">
        <span>Download</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
@import '@design';

.resourceBlockContainer {
  @media (max-width: $lg-breakpoint) {
    flex-shrink: 0;
    margin-right: 16px;
  }

  position: relative;
  height: 100%;
  cursor: pointer;
  background: $white;
  border: 0.5px solid $light-gray;
  border-radius: 8px;
  box-shadow: 3px 3px 6px rgba(196, 196, 196, 0.3);
}

.heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 48px;
  margin: 2px;
  background: $lightest-gray;
  border-radius: 7px;
}

.titleContainer {
  display: flex;
  flex-grow: 1;
  align-items: center;
  margin-left: 8px;

  p {
    padding: 5px 0;
    margin: 0 15px;
  }
}

.heartIcon {
  flex-shrink: 0;
  margin-right: 24px;
}

.body {
  padding: 14px 24px;
}

.footer {
  display: flex;
  justify-content: space-between;

  > div {
    margin: 0 14px 8px 14px;
  }
}

.downloadButton {
  position: relative;
  display: block;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  margin: 16px;
  cursor: pointer;
  border: 1px solid $light-gray;
  border-radius: 50%;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }

  div {
    position: absolute;
    top: 45%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}

.headTitle {
  display: block;
}

.bodyExcerpt {
  @media (max-width: $lg-breakpoint) {
    height: unset;
  }

  display: block;
  height: 72px;
  overflow: hidden;
}

.tagsContainer {
  display: flex;
}

.bodyTitle,
.downloadButtonMinimalInMobile {
  display: none;
}

.minimalDesign {
  display: flex;
  flex-direction: column;

  .headTitle,
  .bodyExcerpt,
  .downloadButton,
  .tagsContainer {
    display: none;
  }

  .bodyTitle {
    display: block;
  }

  .downloadButtonMinimalInMobile {
    box-sizing: border-box;
    display: block;
    padding: 7px 23px;
    border: 1px solid #d4d6d9;
    border-radius: 48px;

    span {
      font-size: 14px;
      font-weight: bold;
    }
  }

  .footer {
    justify-content: center;
    width: 100%;
    margin-top: auto;

    > div {
      margin: 0 8px 8px 8px;
    }
  }
}
</style>
